import { useCallback, useEffect, type Dispatch, type SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

/** `?date=`는 YYYY-MM-DD 정확한 형식만 허용(느슨한 파싱이 만드는 날짜 밀림 차단). */
const DATE_PARAM_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 실재하는 YYYY-MM-DD만 통과시켜 반환(아니면 null).
 *
 * isNaN 체크만으로는 부족하다 — JS가 `2025-02-29`를 3/1로 정규화해 통과시키므로
 * 주소창은 2/29인데 조회·저장은 3/1로 갈린다. 왕복 비교로 실재 날짜만 받는다.
 * 두 effect(날짜 적용 / nickname 스코프)가 같은 판정을 쓰도록 공유한다 — 한쪽만
 * 무효 처리하면 nickname이 영영 매칭되지 않아 URL에 남는다.
 */
function readDateParam(search: string): string | null {
  const raw = new URLSearchParams(search).get('date');
  if (!raw || !DATE_PARAM_RE.test(raw)) return null;
  // 'T00:00:00'을 붙여 로컬 타임존으로 파싱(UTC 해석 시 하루 밀림 방지).
  const parsed = new Date(`${raw}T00:00:00`);
  if (isNaN(parsed.getTime()) || format(parsed, 'yyyy-MM-dd') !== raw) return null;
  return raw;
}

interface UseHomeUrlParamsProps {
  setSelectedDate: Dispatch<SetStateAction<Date>>;
  customerTemplates: CustomerTemplate[];
  setActiveCustomerIdState: (id: string | null) => void;
  user: unknown | null;
  isAuthLoading: boolean;
}

/**
 * URL 쿼리 파라미터 기반 딥링크 진입 처리 훅.
 * - `?date=YYYY-MM-DD` → 해당 날짜로 selectedDate 설정(로컬 타임존 파싱).
 * - `?nickname=...` → customerTemplates에서 가장 최근 주문 고객을 찾아 active로 설정.
 *   적용 성공 시 nickname 파라미터만 URL에서 제거(one-shot 소비).
 *   date는 URL state로 유지 — 날짜 피커도 `?date=`를 쓰고(CustomerTemplateManager
 *   handleDateChange), 새로고침/공유 시 날짜 보존이 기존 계약이다.
 *
 * customerTemplates는 Home 소유 live hub → 인자로 주입(snapshot 금지, 원본 effect deps 보존).
 * location은 Home 다른 곳에서 안 쓰이므로 본 훅이 useLocation을 소유한다.
 */
export function useHomeUrlParams({
  setSelectedDate,
  customerTemplates,
  setActiveCustomerIdState,
  user,
  isAuthLoading,
}: UseHomeUrlParamsProps) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 파라미터가 없거나 무효하면 기준은 '오늘'이다. 예전처럼 selectedDate를 그대로 두면,
    // Home이 마운트를 유지한 채 주소만 `/order`로 바뀌는 경로(현재 탭에서 네비의 /order
    // 링크 클릭)에서 과거 날짜가 URL 흔적도 없이 남아 신규 주문이 거기 저장된다.
    const dateParam = readDateParam(location.search);
    const target = dateParam ?? format(new Date(), 'yyyy-MM-dd');
    // 같은 날짜면 prev 참조를 유지(React bail-out) — nickname 소비 등으로 search가
    // 바뀌어 재실행돼도 불필요한 selectedDate 재설정(주문 재조회 유발)이 없다.
    setSelectedDate(prev => format(prev, 'yyyy-MM-dd') === target ? prev : new Date(`${target}T00:00:00`));
  }, [location.search, setSelectedDate]);

  useEffect(() => {
    // 인증 확정 전에는 소비 금지 — 하드로드 시 customerTemplates가 아직 게스트 세션
    // 리스트라, 동명 게스트 템플릿에 nickname을 오소비하고 파라미터를 지워버리면
    // 인증 후 DB 템플릿으로 교체될 때 교정할 수단이 사라진다(one-shot 소비의 대가).
    if (isAuthLoading) return;
    const params = new URLSearchParams(location.search);
    // URLSearchParams.get()이 이미 percent-decoding 수행 — decodeURIComponent를
    // 다시 적용하면 '%' 포함 닉네임에서 URIError로 crash.
    const nicknameParam = params.get('nickname');
    if (nicknameParam && customerTemplates.length > 0) {
      const dateParam = readDateParam(location.search);
      // 날짜 effect가 '없거나 무효면 오늘'로 확정하므로 nickname 스코프도 같은 날짜를 쓴다.
      // null을 무제한 매칭으로 두면, 과거 날짜 템플릿이 아직 남아 있는 전환 순간에 그 과거
      // 주문로 nickname을 오소비하고 파라미터까지 지워 교정 수단이 사라진다. 스코프를 고정하면
      // 의도한 날짜의 템플릿이 도착할 때까지 소비가 보류된다.
      // (앱이 만드는 nickname 딥링크는 UnifiedOrderSearchCard·OrderStatusSummary 둘 다
      //  항상 date를 동반하므로 실사용 경로의 동작은 그대로다.)
      const targetDate = dateParam ?? format(new Date(), 'yyyy-MM-dd');
      // 가장 최근 주문을 찾기 위해 정렬. 로그인 상태에선 실제 주문(order_ id)만 + dateParam
      // 있으면 해당 날짜만 매칭 — (a) 마운트 중 딥링크 재진입 시 이전 날짜 동명 고객,
      // (b) 인증 직후 DB fetch 완료 전 잔존하는 기본('고객 닉네임')/temp 템플릿 오소비 방지.
      // 딥링크(대시보드/주문현황)는 항상 실제 주문 행에서 생성되므로 order_ 한정이 정확.
      // 게스트는 제외: 세션 템플릿은 order_ id도 아니고 order_date도 날짜 스코프가 아님.
      const targetCustomer = customerTemplates
        .filter(c => c.nickname === nicknameParam &&
          (!user || (c.id.startsWith('order_') && c.order_date === targetDate)))
        .sort((a, b) => (b.createdAt && a.createdAt) ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : 0)[0];

      if (targetCustomer) {
        // 이 render가 캡처한 search는 다른 writer(goToToday)가 그 사이 바꿨을 수 있다.
        // 낡은 스냅샷으로 활성 고객을 고르거나 소비된 nickname을 되쓰지 않도록, 커밋
        // 시점의 실제 주소와 다르면 중단한다. location.search가 dep이라 새 값으로 곧
        // 재실행되므로 딥링크가 유실되지는 않는다.
        const live = new URLSearchParams(window.location.search);
        if (live.get('nickname') !== nicknameParam || readDateParam(window.location.search) !== dateParam) return;
        setActiveCustomerIdState(targetCustomer.id);
        // 딥링크 소비(nickname 제거)는 로그인 상태에서만. 파라미터가 URL에 남으면
        // customerTemplates가 바뀔 때마다(아이템 추가 등) 이 effect가 재발화해
        // 활성 고객을 딥링크 대상으로 되돌린다(고객 문의 버그의 원인 — 로그인 셀러의
        // 대시보드 딥링크 흐름). 게스트 매칭은 소비하지 않음: 비로그인으로 딥링크를
        // 열었다가 나중에 로그인하는 경우, 파라미터가 남아 있어야 DB 템플릿 기준으로
        // (order_ 게이트를 거쳐) 의도한 주문이 재선택된다.
        // date는 유지 — 재실행되는 date effect는 위의 same-day 가드로 no-op.
        if (user) {
          live.delete('nickname');
          navigate({ pathname: location.pathname, search: live.toString() }, { replace: true });
        }
      }
    }
  }, [location.search, location.pathname, customerTemplates, setActiveCustomerIdState, navigate, user, isAuthLoading]);

  // 오늘로 복귀 + `?date=` 제거. 파라미터를 함께 지우지 않으면 위 date effect가 과거 날짜를
  // 즉시 재주입해 복귀가 되돌려진다(고착 사고의 핵심 — 주소창이 만료 없는 저장소로 동작).
  // 캡처된 location이 아니라 호출 시점의 실제 주소에서 읽는다 — 그래야 nickname 소비와
  // 교차해도 이미 소비된 파라미터를 되쓰지 않고, search/pathname 스냅샷에 의존하지 않아
  // 콜백 참조가 안정된다(memo된 OrderDateNotice의 prop 안정성).
  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
    const params = new URLSearchParams(window.location.search);
    if (!params.has('date')) return;
    params.delete('date');
    navigate({ pathname: window.location.pathname, search: params.toString() }, { replace: true });
  }, [setSelectedDate, navigate]);

  return { goToToday };
}
